import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, Cell } from "recharts";
import { Pencil, Check, Search, TrendingUp, Target, Trophy, Users, FileText, Mail, Phone, MailCheck, X, LayoutDashboard, ListTodo, Maximize2, Receipt, BadgeDollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getSalesAnalytics, updateSalesGoals, getAccountManagers, getLeadFollowUps, getProposalNextActions, getLeadPipeline, getProposalPipeline, getProposalFollowupDraft, markProposalFollowupSent, getSalesAlerts, dismissSalesAlert, dismissAllSalesAlerts, getCommissionsAccess } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import CommissionsDashboard from "@/pages/CommissionsDashboard";
import { SortableHeader } from "@/components/common/SortHeader";
import useRefreshTick from "@/hooks/useRefreshTick";
import usePageScrollLane from "@/hooks/usePageScrollLane";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PillRail, Pill } from "@/components/common/Segmented";
import { ChevronDown, ChevronRight } from "lucide-react";


const CUST_COLS = [["customer", "Customer"], ["decided", "Bid"], ["won", "Won"], ["rate_count", "Hit % (count)"], ["rate_amt", "Hit % ($)"], ["won_amt", "Won $"], ["bid_amt", "Total Bid $"]];

// Collapsible dashboard group — slim chevron label row above the cards.
function Group({ id, label, collapsed, onToggle, children }) {
  return (
    <section className="space-y-2" data-testid={`sales-section-${id}`}>
      <button type="button" onClick={() => onToggle(id)} data-testid={`toggle-sales-section-${id}`}
        className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-200 transition-colors">
        {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {label}
      </button>
      {!collapsed && children}
    </section>
  );
}

function PriorityList({ items }) {
  return items.length ? (
    <div className="divide-y divide-slate-100">
      {items.map(it => (
        <div
          key={it.id}
          className="flex items-center gap-3 py-1.5 cursor-pointer rounded-md hover:bg-slate-50 transition-colors"
          onClick={it.go}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") it.go(); }}
          data-testid={`priority-item-${it.id}`}
        >
          <span className={`w-2 h-2 rounded-full shrink-0 ${it.overdue ? "bg-red-500" : "bg-amber-400"}`} />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-700 truncate">{it.title}</div>
            <div className="text-[10px] text-slate-400 truncate flex items-center gap-1.5 flex-wrap">
              {it.customer && (
                <button
                  type="button"
                  className="text-blue-600 font-medium hover:underline"
                  onClick={(e) => { e.stopPropagation(); it.customerGo(); }}
                  data-testid={`priority-customer-${it.id}`}
                >{it.customer}</button>
              )}
              {it.contactName && <span className="text-slate-500">{it.contactName}</span>}
              {it.contactPhone && (
                <a href={`tel:${it.contactPhone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-0.5 text-emerald-700 font-medium hover:underline" data-testid={`priority-phone-${it.id}`}>
                  <Phone className="w-2.5 h-2.5" />{it.contactPhone}
                </a>
              )}
              {it.sub && <span>{it.sub}</span>}
              {it.due && <span>· due {it.due.slice(0, 10)}</span>}
            </div>
            {(it.latestNote || it.lastFollowupAt || it.nextAutoFollowup || it.followupsPaused) && (
              <div className="text-[10px] truncate flex items-center gap-1.5 flex-wrap mt-0.5" data-testid={`priority-followup-info-${it.id}`}>
                {it.latestNote && (
                  <span className="text-amber-700 truncate max-w-[320px]" title={it.latestNote.text}>
                    "{it.latestNote.text}"{it.latestNote.date ? ` (${it.latestNote.date.slice(5)})` : ""}
                  </span>
                )}
                {it.lastFollowupAt && (
                  <span className="text-slate-500">Last follow-up {String(it.lastFollowupAt).slice(0, 10)}{it.followupCount ? ` (#${it.followupCount})` : ""}</span>
                )}
                {!it.lastFollowupAt && typeof it.followupCount === "number" && it.followupCount === 0 && (
                  <span className="text-slate-400">No follow-ups sent yet</span>
                )}
                {it.followupsPaused
                  ? <span className="text-slate-400">· auto follow-ups off</span>
                  : it.nextAutoFollowup && <span className="text-blue-600">· next auto {it.nextAutoFollowup}</span>}
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" className="h-6 text-[10px] text-blue-700 border-blue-200 hover:bg-blue-50" onClick={(e) => { e.stopPropagation(); it.sendEmail(); }} data-testid={`priority-email-${it.id}`}>
            <Mail className="w-3 h-3 mr-1" /> Email
          </Button>
        </div>
      ))}
    </div>
  ) : <div className="text-xs text-slate-400 py-2">No lead or proposal follow-ups due</div>;
}

// iter249 — Only render Commissions section for users the server allows.
// Uses the lightweight /api/commissions/access probe (no 403 spam).
function CommissionsSection() {
  const [collapsed, setCollapsed] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    getCommissionsAccess()
      .then(r => setAllowed(!!r?.data?.allowed))
      .catch(() => setAllowed(false))
      .finally(() => setChecked(true));
  }, []);
  if (!checked || !allowed) return null;
  return (
    <Group id="commissions" label="Commissions" collapsed={collapsed} onToggle={() => setCollapsed(c => !c)}>
    <div className="mt-2 pt-6 border-t border-slate-200" data-testid="sales-commissions-section">
      <CommissionsDashboard embedded />
    </div>
    </Group>
  );
}

const fmt$ = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);
const fmtK = (v) => (Math.abs(v) >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${Math.round(v / 1000)}K`);

function GoalEditable({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  if (!editing) return (
    <button className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600" onClick={() => setEditing(true)} data-testid={testid}>
      Goal: <b>{fmt$(value)}</b><Pencil className="w-3 h-3" />
    </button>
  );
  return (
    <span className="inline-flex items-center gap-1">
      <Input type="number" value={val} onChange={e => setVal(e.target.value)} className="h-6 w-28 text-xs" data-testid={`${testid}-input`} />
      <button onClick={() => { onSave(parseFloat(val) || 0); setEditing(false); }} data-testid={`${testid}-save`}>
        <Check className="w-3.5 h-3.5 text-green-600" />
      </button>
    </span>
  );
}

function StatChips({ stats }) {
  const chips = [
    ["4-wk avg", stats.t4w_avg], ["3-mo avg", stats.m3_avg], ["6-mo avg", stats.m6_avg],
    ["12-mo avg", stats.m12_avg], ["FY avg", stats.fy_avg], ["Last FY avg", stats.last_fy_avg], ["FY total", stats.fy_total],
  ];
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {chips.map(([l, v]) => (
        <span key={l} className="text-[10px] px-2 py-0.5 bg-slate-100 rounded-full text-slate-600">
          {l}: <b className="tabular-nums">{fmtK(v || 0)}</b>
        </span>
      ))}
    </div>
  );
}

const BID_COLORS = { pending: "#eab308", lost: "#dc2626", won: "#16a34a" };
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (m) => `${MONTH_ABBR[parseInt(m.slice(5, 7), 10) - 1]} '${m.slice(2, 4)}`;
const weekLabel = (w) => {
  // w = 'YYYY-MM-DD' (Monday of the week). Show as 'Aug 3'.
  const [y, m, d] = w.split("-").map(Number);
  return `${MONTH_ABBR[m - 1]} ${d}`;
};
const quarterLabel = (q) => {
  // q = 'YYYY-MM-DD' (first of quarter start month). FY = May–Apr.
  const [y, m] = q.split("-").map(Number);
  const fyStartYear = m >= 5 ? y : y - 1;
  const qIdx = Math.floor(((y - fyStartYear) * 12 + (m - 5)) / 3); // 0..3
  return `Q${qIdx + 1} '${String((fyStartYear + 1) % 100).padStart(2, "0")}`;
};

function RevenueChart({ data, periodKey, mode, goal, testid, variant }) {
  const gm = mode === "gm";
  const rows = (data || []).map(d => {
    const label = periodKey === "week"
      ? weekLabel(d.week)
      : periodKey === "quarter"
        ? quarterLabel(d.quarter)
        : monthLabel(d.month);
    if (variant === "bid") {
      return gm
        ? { label, pending: d.gm_pending || 0, lost: d.gm_lost || 0, won: d.gm_won || 0 }
        : { label, pending: d.pending || 0, lost: d.lost || 0, won: d.won || 0 };
    }
    return { label, total: (gm ? d.gm_total : d.total) || 0 };
  });
  const stacked = variant === "bid";
  const axisTitle = periodKey === "week" ? "Week (Mon)" : periodKey === "quarter" ? "Fiscal Quarter" : "Month";
  return (
    <div data-testid={testid}>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 22 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#475569" }}
              interval="preserveStartEnd"
              label={{ value: axisTitle, position: "insideBottom", offset: -14, fontSize: 10, fill: "#64748b" }}
            />
            <YAxis tick={{ fontSize: 9 }} tickFormatter={fmtK} width={44} />
            <Tooltip
              formatter={(v, name) => [fmt$(v), name]}
              labelFormatter={(l) => `${axisTitle}: ${l}`}
            />
            {goal != null && <ReferenceLine y={goal} stroke="#64748b" strokeDasharray="4 4" label={{ value: `Goal ${fmtK(goal)}`, fontSize: 9, fill: "#64748b", position: "insideTopRight" }} />}
            {stacked ? (
              <>
                {/* stack bottom→top: open (yellow), lost (red), won (green) */}
                <Bar dataKey="pending" name={gm ? "Open GM$" : "Open bids"} stackId="bid" fill={BID_COLORS.pending} />
                <Bar dataKey="lost" name="Lost" stackId="bid" fill={BID_COLORS.lost} />
                <Bar dataKey="won" name="Won" stackId="bid" fill={BID_COLORS.won} radius={[3, 3, 0, 0]} />
              </>
            ) : (
              <Bar dataKey="total" name={gm ? "GM$ booked" : "Booked"} fill="#16a34a" radius={[3, 3, 0, 0]} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {stacked && (
        <div className="flex gap-3 justify-end mt-1 text-[10px] text-slate-500" data-testid={`${testid}-legend`}>
          {[["Open bids", BID_COLORS.pending], ["Lost", BID_COLORS.lost], ["Won", BID_COLORS.won]].map(([l, c]) => (
            <span key={l} className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: c }} />{l}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function TogglePills({ options, value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)}
          className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${value === v ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
          data-testid={`${testid}-${v}`}>{l}</button>
      ))}
    </div>
  );
}

// iter246 — Revenue Bid / Booked card: Weekly⇄Monthly⇄Quarterly + Rev$⇄GM$ toggles.
// The configured goal is ANNUAL; weekly = annual/52, monthly = annual/12,
// quarterly = annual/4 (all computed server-side, incl. per-AM 25/75 split).
// iter247 — YTD progress bar shown under the header (fy_total ÷ annual goal).
function RevenueCard({ title, dateBasis, block, variant, testid, am, onSaveAnnualGoal }) {
  const [period, setPeriod] = useState("weekly");
  const [mode, setMode] = useState("rev");
  const data = period === "weekly" ? block.weeks : period === "monthly" ? block.months : block.quarters;
  const periodKey = period === "weekly" ? "week" : period === "monthly" ? "month" : "quarter";
  const goal = mode === "rev"
    ? (period === "weekly" ? block.goal_weekly : period === "monthly" ? block.goal_monthly : block.goal_quarterly)
    : null;
  const ytdPct = Math.min(999, block.ytd_pct ?? 0);
  const overGoal = ytdPct >= 100;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold text-slate-700 flex items-center gap-2" data-testid={`${testid}-title`}>{variant === "bid" ? <Receipt className="w-4 h-4 text-sky-500" /> : <BadgeDollarSign className="w-4 h-4 text-emerald-500" />}{title}</div>
        <div className="flex items-center gap-3 flex-wrap">
          <TogglePills options={[["weekly", "Weekly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"]]} value={period} onChange={setPeriod} testid={`${testid}-period`} />
          <TogglePills options={[["rev", "Rev $"], ["gm", "GM $"]]} value={mode} onChange={setMode} testid={`${testid}-mode`} />
          {mode === "rev" && (
            am ? (
              <span className="text-xs text-slate-500" data-testid={`${testid}-goal-split`}>
                Goal: <b className="text-slate-700">{fmt$(goal)}</b>
                <span className="ml-1 text-slate-400">({block.goal_pct}% of company · {period})</span>
              </span>
            ) : (
              <span className="text-xs text-slate-500 flex items-center gap-1" data-testid={`${testid}-goal`}>
                <span className="text-slate-400">Annual</span>
                <GoalEditable value={block.goal_annual} testid={`${testid}-goal-annual`} onSave={onSaveAnnualGoal} />
                <span className="text-slate-400">→ {fmt$(goal)}/{period === "weekly" ? "wk" : period === "monthly" ? "mo" : "qtr"}</span>
              </span>
            )
          )}
        </div>
      </div>
      {/* YTD progress vs annual goal */}
      {mode === "rev" && (
        <div className="mt-2" data-testid={`${testid}-ytd`}>
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <span>YTD: <b className="tabular-nums text-slate-700">{fmtK(block.ytd || 0)}</b> of <span className="tabular-nums">{fmtK(block.goal_annual || 0)}</span></span>
            <span className={`tabular-nums font-semibold ${overGoal ? "text-emerald-600" : "text-slate-600"}`}>{ytdPct}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
            <div className={`h-1.5 rounded-full ${overGoal ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, ytdPct)}%` }} />
          </div>
        </div>
      )}
      <RevenueChart data={data} periodKey={periodKey} mode={mode} goal={goal} testid={`chart-${testid}`} variant={variant} />
      <StatChips stats={block.stats} />
    </div>
  );
}

function HitRateBlock({ label, b }) {
  return (
    <div className="flex-1 border border-slate-200 rounded-lg p-3">
      <div className="text-xs font-semibold text-slate-500 uppercase mb-2">{label}</div>
      {[["By count", b.rate_count, `${b.won_n} won of ${b.decided_n} bids`],
        ["By $", b.rate_amt, `${fmtK(b.won_amt)} of ${fmtK(b.decided_amt)}`],
        ["By GM $", b.rate_gm ?? 0, `${fmtK(b.won_gm || 0)} of ${fmtK(b.decided_gm || 0)}`]].map(([l, pct, sub]) => (
        <div key={l} className="mb-2">
          <div className="flex justify-between text-xs"><span className="text-slate-500">{l}</span><b className="tabular-nums" data-testid={`hitrate-${label.toLowerCase().replace(/\s+/g, "-")}-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/g, "")}`}>{pct}%</b></div>
          <div className="h-1.5 bg-slate-100 rounded-full mt-1"><div className="h-1.5 bg-blue-500 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} /></div>
          <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>
        </div>
      ))}
    </div>
  );
}

function PipelineCard({ title, icon, accent, stages, unit, testid }) {
  const totalCount = stages.reduce((s, x) => s + x.count, 0);
  const totalValue = stages.reduce((s, x) => s + x.total_value, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">{icon}{title}</div>
      {stages.length ? (
        <>
          <div className={`px-3 py-2 mb-2 rounded-lg border ${accent}`} data-testid={`${testid}-totals`}>
            <div className="flex items-center justify-between text-xs">
              <span>Total: <b className="text-sm">{totalCount}</b></span>
              <span>Value: <b className="text-sm">{fmt$(totalValue)}</b></span>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {stages.map(st => (
              <div key={st.status} className="flex items-center justify-between py-1.5 text-xs" data-testid={`${testid}-stage-${st.status}`}>
                <span className="font-medium text-slate-700">
                  {st.status === "bid_submitted" ? "Bid Submitted" : st.status.charAt(0).toUpperCase() + st.status.slice(1)}
                  <span className="text-slate-400 ml-2">{st.count} {unit}{st.count === 1 ? "" : "s"}</span>
                </span>
                <span className="tabular-nums font-semibold">{fmt$(st.total_value)}</span>
              </div>
            ))}
          </div>
        </>
      ) : <div className="text-xs text-slate-400 py-2">Nothing in the pipeline yet</div>}
    </div>
  );
}

export default function SalesDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ams, setAms] = useState([]);
  const [am, setAm] = useState(null); // null until AMs load
  const [data, setData] = useState(null);
  const [leadFollowUps, setLeadFollowUps] = useState([]);
  const [proposalActions, setProposalActions] = useState([]);
  const [custPeriod, setCustPeriod] = useState("all");
  const [custSearch, setCustSearch] = useState("");
  const [custSort, setCustSort] = useState({ key: "bid_amt", dir: "desc" });
  const [leadPipeline, setLeadPipeline] = useState([]);
  const [proposalPipeline, setProposalPipeline] = useState([]);
  const [salesAlerts, setSalesAlerts] = useState([]);
  const [priorityFull, setPriorityFull] = useState(false);
  const refreshTick = useRefreshTick();
  const laneRef = usePageScrollLane(0.25);

  useEffect(() => {
    (async () => {
      try {
        const [amRes, lfRes, paRes, lpRes, ppRes, alRes] = await Promise.all([
          getAccountManagers().catch(() => ({ data: [] })), getLeadFollowUps().catch(() => ({ data: [] })),
          getProposalNextActions().catch(() => ({ data: [] })),
          getLeadPipeline().catch(() => ({ data: [] })), getProposalPipeline().catch(() => ({ data: [] })),
          getSalesAlerts().catch(() => ({ data: [] })),
        ]);
        const names = (amRes.data || []).filter(a => a.active !== false).map(a => a.name);
        setAms(names);
        // Default the toggle to the logged-in user when they're an AM.
        const mine = names.find(n => n && user?.name && n.toLowerCase() === user.name.toLowerCase());
        setAm(mine || "");
        setLeadFollowUps(lfRes.data || []);
        setProposalActions(paRes.data || []);
        setLeadPipeline(lpRes.data || []);
        setProposalPipeline(ppRes.data || []);
        setSalesAlerts(alRes.data || []);
      } catch { setAm(""); }
    })();
  }, [user]);

  useEffect(() => {
    if (am === null) return;
    getSalesAnalytics(am).then(r => setData(r.data)).catch((e) => { if (!refreshTick) toast.error(e?.response?.data?.detail ? `Failed to load sales analytics: ${e.response.data.detail}` : "Failed to load sales analytics"); });
  }, [am, refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-pull follow-ups/pipelines when the tab regains focus or every minute.
  useEffect(() => {
    if (!refreshTick) return;
    getLeadFollowUps().then(r => setLeadFollowUps(r.data || [])).catch(() => {});
    getProposalNextActions().then(r => setProposalActions(r.data || [])).catch(() => {});
    getLeadPipeline().then(r => setLeadPipeline(r.data || [])).catch(() => {});
    getProposalPipeline().then(r => setProposalPipeline(r.data || [])).catch(() => {});
    getSalesAlerts().then(r => setSalesAlerts(r.data || [])).catch(() => {});
  }, [refreshTick]);

  const saveGoal = async (patch) => {
    try {
      await updateSalesGoals(patch);
      const r = await getSalesAnalytics(am);
      setData(r.data);
      toast.success("Goal updated");
    } catch { toast.error("Failed to save goal"); }
  };

  const custRows = useMemo(() => {
    const q = custSearch.trim().toLowerCase();
    return (data?.hit_by_customer || [])
      .filter(r => r[custPeriod].decided > 0)
      .filter(r => !q || r.customer.toLowerCase().includes(q))
      .sort((a, b) => {
        const { key, dir } = custSort;
        const d = key === "customer" ? a.customer.localeCompare(b.customer) : a[custPeriod][key] - b[custPeriod][key];
        return dir === "asc" ? d : -d;
      });
  }, [data, custPeriod, custSearch, custSort]);
  const sortCust = (key) => setCustSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "customer" ? "asc" : "desc" });

  const priorityItems = useMemo(() => {
    const items = [];
    const openEmail = (to, subject, body) => {
      window.location.href = `mailto:${to || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };
    leadFollowUps.forEach(l => items.push({
      id: `lead-${l.id}`, title: `Lead Follow Up: ${l.company_name}`,
      customer: l.company_name,
      customerGo: () => navigate("/leads"),
      contactName: l.contact_name, contactPhone: l.phone, contactEmail: l.email,
      due: l.next_follow_up, overdue: l.next_follow_up && new Date(l.next_follow_up) < new Date(),
      latestNote: (l.notes || [])[0] || (l.follow_up_notes ? { date: "", text: l.follow_up_notes } : null),
      go: () => navigate("/leads"),
      sendEmail: () => {
        const first = (l.contact_name || "").split(" ")[0];
        openEmail(l.email,
          `Following up — ${l.company_name}`,
          `Hi ${first || "there"},\n\nI wanted to follow up with you regarding ${l.company_name}. Please let me know if you have any questions or if there is anything we can help with.\n\nThank you,\nABS Inc.`);
        toast.success("Email opened in your mail app");
      },
    }));
    proposalActions.forEach(p => items.push({
      id: `prop-${p.id}`, title: `${p.next_action_desc || "Proposal Follow Up"}: ${p.proposal_name}`,
      customer: p.client_name, sub: `${fmt$(p.value)} (${p.probability}%)`,
      customerGo: () => navigate("/clients", { state: { openClientId: p.client_id } }),
      contactName: p.contact_name, contactPhone: p.contact_phone, contactEmail: p.contact_email,
      due: p.next_action_date, overdue: p.is_overdue,
      followupCount: p.followup_count, lastFollowupAt: p.last_followup_at,
      nextAutoFollowup: p.next_auto_followup, followupsPaused: p.followups_paused,
      latestNote: p.latest_note,
      go: () => navigate("/proposals", { state: { viewProposalId: p.id } }),
      sendEmail: async () => {
        try {
          const res = await getProposalFollowupDraft(p.id);
          const d = res.data || {};
          openEmail(d.to || p.contact_email, d.subject || "", d.body || "");
          markProposalFollowupSent(p.id).catch(() => {});
          toast.success("Follow-up email opened in your mail app");
        } catch { toast.error("Failed to build follow-up email"); }
      },
    }));
    return items.sort((a, b) => (a.due || "").localeCompare(b.due || ""));
  }, [leadFollowUps, proposalActions, navigate]);

  const mb = data?.month_bookings;

  return (
    <div ref={laneRef} className="space-y-5" data-testid="sales-dashboard">
      <div className="flex items-center gap-2 flex-wrap" data-testid="sales-dashboard-header">
        <PillRail testId="am-toggle">
          <Pill on={am === ""} onClick={() => setAm("")} testId="am-toggle-all">All AMs</Pill>
          {ams.map(n => (
            <Pill key={n} on={am === n} onClick={() => setAm(n)} testId={`am-toggle-${n.split(" ")[0]}`}>{n.split(" ")[0]}</Pill>
          ))}
        </PillRail>
      </div>

      {/* Customer Reply Alerts (iter243) */}
      {salesAlerts.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="reply-alerts-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
              <MailCheck className="w-4 h-4 text-emerald-600" /> Customer Replies
              <span className="text-[10px] font-bold bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded-full">{salesAlerts.length}</span>
            </div>
            <button
              type="button"
              className="text-[10px] text-emerald-700 font-medium hover:underline"
              onClick={async () => { await dismissAllSalesAlerts().catch(() => {}); setSalesAlerts([]); }}
              data-testid="reply-alerts-dismiss-all"
            >Dismiss All</button>
          </div>
          <div className="divide-y divide-emerald-100">
            {salesAlerts.map(a => (
              <div key={a.id} className="flex items-center gap-3 py-1.5" data-testid={`reply-alert-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-700 truncate">
                    {a.from} replied on <b>{a.proposal_name}</b> — follow-ups auto-stopped
                  </div>
                  <div className="text-[10px] text-slate-400 truncate">
                    {a.client_name}{a.am_name ? ` · AM: ${a.am_name}` : ""} · {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
                <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => navigate("/proposals", { state: { viewProposalId: a.proposal_id } })} data-testid={`reply-alert-open-${a.id}`}>Open</Button>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-emerald-100 text-emerald-600"
                  title="Dismiss"
                  onClick={async () => { await dismissSalesAlert(a.id).catch(() => {}); setSalesAlerts(prev => prev.filter(x => x.id !== a.id)); }}
                  data-testid={`reply-alert-dismiss-${a.id}`}
                ><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Priority actions — lead + proposal items (moved from Ops Dashboard) */}
      <section className="space-y-4" data-testid="sales-section-priority">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4 h-[340px] flex flex-col lg:order-2" data-testid="sales-priority-actions">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-700 flex items-center gap-2"><ListTodo className="w-4 h-4 text-rose-500" />Priority Actions
            {priorityItems.length > 0 && <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{priorityItems.length}</span>}
          </div>
          <button type="button" onClick={() => setPriorityFull(true)} title="Expand to full screen" className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid="priority-expand-btn"><Maximize2 className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto"><PriorityList items={priorityItems} /></div>
        <Dialog open={priorityFull} onOpenChange={setPriorityFull}>
          <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] flex flex-col" data-testid="priority-fullscreen">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ListTodo className="w-4 h-4 text-rose-500" />Priority Actions ({priorityItems.length})</DialogTitle></DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto"><PriorityList items={priorityItems} /></div>
          </DialogContent>
        </Dialog>
      </div>
      {mb && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 h-[340px] flex flex-col lg:order-1" data-testid="card-month-bookings">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Month Bookings</div>
                <GoalEditable value={mb.goal} testid="goal-monthly"
                  onSave={(v) => saveGoal(am ? { monthly_by_am: { [am]: v } } : { monthly_company: v })} />
              </div>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-2xl font-bold tabular-nums" data-testid="month-bookings-total">{fmt$(mb.total)}</span>
                <span className={`text-sm font-semibold ${mb.pct >= 100 ? "text-green-600" : "text-slate-500"}`}>{mb.pct}% to goal</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full mt-2">
                <div className={`h-2 rounded-full ${mb.pct >= 100 ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, mb.pct)}%` }} />
              </div>
              <div className="mt-3 flex-1 min-h-0 overflow-y-auto divide-y divide-slate-100" data-testid="month-bookings-wos">
                {mb.work_orders.map(w => (
                  <button type="button" key={w.id} className="w-full flex items-center gap-2 py-1.5 text-xs text-left hover:bg-slate-50 rounded"
                    onClick={() => navigate("/clients", { state: { openClientId: w.client_id, openTab: "workorders", openWorkOrderId: w.id } })}
                    data-testid={`month-booking-wo-${w.id}`}>
                    <span className="font-mono font-semibold text-blue-600 w-24 truncate">{w.wo_number}</span>
                    <span className="text-slate-500 w-40 truncate">{w.customer || "—"}</span>
                    <span className="flex-1 truncate font-medium text-slate-700">{w.title}</span>
                    <span className="text-slate-400 w-14 truncate">{w.am ? w.am.split(" ")[0] : "—"}</span>
                    <span className="tabular-nums w-24 text-right font-semibold">{fmt$(w.amount)}</span>
                  </button>
                ))}
                {!mb.work_orders.length && <div className="text-xs text-slate-400 py-2">No work orders booked yet this month</div>}
              </div>
              {mb.work_orders.length > 0 && (
                <div className="flex items-center gap-2 pt-2 border-t border-slate-200 text-xs font-semibold text-slate-700" data-testid="month-bookings-wo-total">
                  <span className="flex-1">{mb.work_orders.length} work order{mb.work_orders.length === 1 ? "" : "s"}</span>
                  <span className="tabular-nums w-24 text-right">{fmt$(mb.total)}</span>
                </div>
              )}
            </div>
      )}
      </div>
      </section>

      {/* Pipelines — moved from Ops Dashboard */}
      <section className="space-y-4" data-testid="sales-section-pipelines">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PipelineCard title="Lead Pipeline" icon={<TrendingUp className="w-4 h-4 text-blue-500" />}
          accent="bg-blue-50 border-blue-100 text-blue-700" stages={leadPipeline} unit="lead" testid="lead-pipeline" />
        <PipelineCard title="Proposal Pipeline" icon={<FileText className="w-4 h-4 text-orange-500" />}
          accent="bg-orange-50 border-orange-100 text-orange-700" stages={proposalPipeline} unit="proposal" testid="proposal-pipeline" />
      </div>
      </section>

      {data && (
        <>
          <section className="space-y-4" data-testid="sales-section-revenue">
          {/* 2 — Revenue bid (weekly/monthly · Rev$/GM$) */}
          <RevenueCard
            title="Revenue Bid"
            dateBasis="bid due date"
            block={data.weekly_bid}
            variant="bid"
            testid="card-revenue-bid"
            am={am}
            onSaveAnnualGoal={(v) => saveGoal({ weekly_bid: v })}
          />

          {/* 3 — Revenue booked (weekly/monthly · Rev$/GM$) */}
          <RevenueCard
            title="Revenue Booked"
            dateBasis="won date"
            block={data.weekly_booked}
            variant="booked"
            testid="card-revenue-booked"
            am={am}
            onSaveAnnualGoal={(v) => saveGoal({ weekly_booked: v })}
          />
          </section>

          {/* 5 — Hit rate by customer */}
          <section className="space-y-4" data-testid="sales-section-customers">
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="card-hit-rates">
              <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-500" />Hit Rates</div>
              <div className="flex gap-3">
                <HitRateBlock label="All Time" b={data.hit_rates.all_time} />
                <HitRateBlock label="Last FY" b={data.hit_rates.last_fy} />
                <HitRateBlock label="This FY" b={data.hit_rates.this_fy} />
              </div>
            </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="card-hit-by-customer">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4 text-purple-500" />Hit Rate by Customer</div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
                  <Input value={custSearch} onChange={e => setCustSearch(e.target.value)} placeholder="Search customer" className="h-7 pl-7 text-xs w-44" data-testid="cust-search" />
                </div>
                {[["all", "All Time"], ["last_fy", "Last FY"], ["this_fy", "This FY"]].map(([v, l]) => (
                  <button key={v} onClick={() => setCustPeriod(v)}
                    className={`px-2.5 py-1 rounded-full text-[11px] border ${custPeriod === v ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}
                    data-testid={`cust-period-${v}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="mt-3 overflow-auto max-h-[348px]" data-testid="cust-table-scroll">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="h-7 text-left text-[10px] uppercase text-slate-400 border-b">
                    {CUST_COLS.map(([key, label]) => (
                      <th key={key} className={key === "customer" ? "pr-2" : "text-right px-2"} title={key === "decided" ? "Decided bids — won + lost proposals; open bids are excluded so the rate is final" : undefined}>
                        <SortableHeader label={label} sorted={custSort.key === key} direction={custSort.dir} onSort={() => sortCust(key)} testId={`cust-sort-${key}`} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {custRows.map(r => {
                    const b = r[custPeriod];
                    return (
                      <tr key={r.customer} className="h-7 border-b border-slate-50 hover:bg-slate-50">
                        <td className="pr-2 font-medium text-slate-700">{r.customer}</td>
                        <td className="text-right px-2 tabular-nums">{b.decided}</td>
                        <td className="text-right px-2 tabular-nums">{b.won}</td>
                        <td className="text-right px-2 tabular-nums font-semibold">{b.rate_count}%</td>
                        <td className="text-right px-2 tabular-nums">{b.rate_amt}%</td>
                        <td className="text-right px-2 tabular-nums text-green-600">{fmt$(b.won_amt)}</td>
                        <td className="text-right px-2 tabular-nums">{fmt$(b.bid_amt)}</td>
                      </tr>
                    );
                  })}
                  {!custRows.length && <tr><td colSpan={7} className="py-3 text-slate-400">No decided projects in this period</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          </section>

          {/* iter249 — Commissions Dashboard embedded at the bottom of Sales.
              Server locks /api/commissions/* to Ryan / Nathan / Mary via
              require_commissions_viewer; the component silently no-ops
              (returns null) for other users. */}
          <CommissionsSection />
        </>
      )}
    </div>
  );
}
